import { ReactElement, useRef, useState, useEffect } from 'react'
import { Navigation, Pagination } from 'swiper'
import { Swiper, SwiperRef, SwiperSlide } from 'swiper/react'
import { useRouter } from 'next/router'

import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'

import ProductSliderItem from './../Products/ProductSliderItem'
import ProductThumbnailItem from './../Products/ProductThumbnailItem'

interface Props {
  images: string[]
}

export default function ProductSlider({ images }: Props): ReactElement {
  const {locale} = useRouter();
  const swiperRef = useRef<SwiperRef>(null)
  const [sliderIndex, setSliderIndex] = useState(0)
  const [imagesLoaded, setImagesLoaded] = useState<boolean[]>([])

  useEffect(() => {
    // Initialize all images as not loaded
    setImagesLoaded(Array(images.length).fill(false))
  }, [images])

  const handleImageLoad = (index: number) => {
    setImagesLoaded(prev => {
      const newState = [...prev]
      newState[index] = true
      return newState
    })
  }

  return (
    <div className={`${locale === 'ar' ? 'flex flex-row': 'flex flex-row-reverse'} gap-4 h-[480px] w-full`}>
      {/* Right: Thumbnails (1/6 width) */}
      {images.length > 0 && (
        <div className="w-1/6 max-h-full overflow-y-auto">
          <Swiper
            direction="vertical"
            spaceBetween={10}
            slidesPerView={4}
            modules={[Navigation]}
            className="h-full"
          >
            {images.map((image, idx) => (
              <SwiperSlide key={idx}>
                <ProductThumbnailItem
                  image={image}
                  index={idx}
                  mainRef={swiperRef}
                  sliderIndex={sliderIndex}
                  setSliderIndex={setSliderIndex}
                  onLoad={() => handleImageLoad(idx)}
                  loaded={imagesLoaded[idx]}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      )}
      {/* Left: Main Slider (5/6 width) */}
      <div className={`${images.length > 0 ? 'w-5/6' : 'w-full'} relative`}>
        <Swiper
          ref={swiperRef}
          slidesPerView={1}
          navigation
          pagination={{ clickable: true }}
          modules={[Navigation, Pagination]}
          onSlideChange={(swiper) => setSliderIndex(swiper.activeIndex)}
          className="h-full product-main-swiper"
        >
          {images.map((image, idx) => (
            <SwiperSlide key={idx}>
              <ProductSliderItem 
                image={image} 
                onLoad={() => handleImageLoad(idx)}
                loaded={imagesLoaded[idx]}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </div>
  )
}